export interface DonationType {
  id: string
  name: string
  description: string
  iconName: string
  suggestedAmounts: number[]
  minimumAmount: number
  allowCustomAmount: boolean
  allowRecurring: boolean
  recurringFrequencies: string[]
  isActive: boolean
  order: number
}

export interface DonationCampaign {
  id: string
  title: string
  description: string
  targetAmount: number
  raisedAmount: number
  isActive: boolean
  createdAt: string
  linkedDonationTypeIds: string[] // Links to donation types that contribute to this campaign
}

// Default campaigns matching iOS app mock data
export const DEFAULT_CAMPAIGNS: DonationCampaign[] = [
  {
    id: 'new_masjid_2024',
    title: 'New Masjid Building',
    description: 'Help us build a new masjid for the community. Your contributions will go towards construction, facilities, and creating a welcoming space for worship.',
    targetAmount: 50000,
    raisedAmount: 32500,
    isActive: true,
    createdAt: '2024-01-15',
    linkedDonationTypeIds: ['building-fund'],
  },
]

// Platform types for analytics
// Storage platforms: paypal, bank_transfer, stripe
// Display platforms (from API analytics): apple_pay, google_pay (derived from stripe + device)
export type PaymentPlatform = 'paypal' | 'bank_transfer' | 'stripe' | 'apple_pay' | 'google_pay'

// Device type for Stripe transactions to determine Apple Pay vs Google Pay
export type DeviceType = 'ios' | 'android' | 'web'

export interface PlatformInfo {
  id: PaymentPlatform
  name: string
  provider: string
  description: string
  isManual: boolean // true if donations need to be manually entered
}

export const PAYMENT_PLATFORMS: PlatformInfo[] = [
  {
    id: 'stripe',
    name: 'Stripe',
    provider: 'Stripe',
    description: 'Mobile app donations via Apple Pay / Google Pay',
    isManual: false,
  },
  {
    id: 'apple_pay',
    name: 'Apple Pay',
    provider: 'Stripe',
    description: 'Mobile app donations via Apple Pay (iOS)',
    isManual: false,
  },
  {
    id: 'google_pay',
    name: 'Google Pay',
    provider: 'Stripe',
    description: 'Mobile app donations via Google Pay (Android)',
    isManual: false,
  },
  {
    id: 'paypal',
    name: 'PayPal',
    provider: 'PayPal',
    description: 'Online donations via rossstreetmasjid.org',
    isManual: false,
  },
  {
    id: 'bank_transfer',
    name: 'Bank Transfer',
    provider: 'Direct',
    description: 'Manual bank transfers to masjid accounts',
    isManual: true,
  },
]

// Helper to get display name for platform + device combination
export const getDisplayPlatformName = (platform: PaymentPlatform, device?: DeviceType): string => {
  // Handle display platforms (returned by API analytics)
  if (platform === 'apple_pay') return 'Apple Pay'
  if (platform === 'google_pay') return 'Google Pay'

  // Handle storage platforms with device info (for transaction display)
  if (platform === 'stripe' && device) {
    switch (device) {
      case 'ios':
        return 'Apple Pay'
      case 'android':
        return 'Google Pay'
      case 'web':
        return 'Stripe Web'
    }
  }
  switch (platform) {
    case 'paypal':
      return 'PayPal'
    case 'bank_transfer':
      return 'Bank Transfer'
    case 'stripe':
      return 'Stripe'
    default:
      return platform
  }
}

// Manual donation entry (for bank transfers and other non-trackable sources)
export interface ManualDonation {
  id: string
  amount: number
  donationTypeId: string
  campaignId?: string // optional - link to a campaign
  date: string // ISO date string
  reference?: string // bank reference or notes
  createdAt: string
  createdBy?: string // admin who entered it
}

// Mock manual donations data
export const MOCK_MANUAL_DONATIONS: ManualDonation[] = [
  {
    id: 'manual-1',
    amount: 5000,
    donationTypeId: 'building-fund',
    campaignId: 'new_masjid_2024',
    date: '2024-02-15',
    reference: 'Bank ref: BLDG-2024-001',
    createdAt: '2024-02-16T10:30:00Z',
  },
  {
    id: 'manual-2',
    amount: 2500,
    donationTypeId: 'zakat',
    date: '2024-02-20',
    reference: 'Anonymous donor - Zakat',
    createdAt: '2024-02-21T14:15:00Z',
  },
  {
    id: 'manual-3',
    amount: 1000,
    donationTypeId: 'building-fund',
    campaignId: 'new_masjid_2024',
    date: '2024-03-01',
    reference: 'Monthly standing order',
    createdAt: '2024-03-02T09:00:00Z',
  },
  {
    id: 'manual-4',
    amount: 500,
    donationTypeId: 'jummah',
    date: '2024-03-05',
    reference: 'Bank transfer - Friday collection',
    createdAt: '2024-03-05T16:45:00Z',
  },
]

// Analytics data for campaigns
export interface DonationBreakdown {
  donationTypeId: string
  amount: number
  count: number // number of transactions
  byPlatform?: PlatformBreakdown[] // optional breakdown by platform
}

export interface PlatformBreakdown {
  platform: PaymentPlatform
  amount: number
  count: number
}

export interface CampaignAnalytics {
  campaignId: string
  period: 'all_time' | 'this_month' | 'last_month' | 'custom'
  startDate?: string
  endDate?: string
  totalAmount: number
  totalTransactions: number
  byDonationType: DonationBreakdown[]
  byPlatform: PlatformBreakdown[]
}

// Mock analytics data for the default campaign
export const MOCK_CAMPAIGN_ANALYTICS: Record<string, CampaignAnalytics[]> = {
  'new_masjid_2024': [
    {
      campaignId: 'new_masjid_2024',
      period: 'all_time',
      totalAmount: 39000,
      totalTransactions: 252,
      byDonationType: [
        { donationTypeId: 'building-fund', amount: 30500, count: 185 },
        { donationTypeId: 'jummah', amount: 5500, count: 47 },
        { donationTypeId: 'zakat', amount: 3000, count: 20 },
      ],
      byPlatform: [
        { platform: 'apple_pay', amount: 15000, count: 120 },
        { platform: 'google_pay', amount: 8500, count: 72 },
        { platform: 'paypal', amount: 9000, count: 53 },
        { platform: 'bank_transfer', amount: 6500, count: 7 },
      ],
    },
    {
      campaignId: 'new_masjid_2024',
      period: 'this_month',
      totalAmount: 5700,
      totalTransactions: 35,
      byDonationType: [
        { donationTypeId: 'building-fund', amount: 4500, count: 27 },
        { donationTypeId: 'jummah', amount: 800, count: 6 },
        { donationTypeId: 'zakat', amount: 400, count: 2 },
      ],
      byPlatform: [
        { platform: 'apple_pay', amount: 2000, count: 15 },
        { platform: 'google_pay', amount: 1200, count: 10 },
        { platform: 'paypal', amount: 1000, count: 7 },
        { platform: 'bank_transfer', amount: 1500, count: 3 },
      ],
    },
    {
      campaignId: 'new_masjid_2024',
      period: 'last_month',
      totalAmount: 4800,
      totalTransactions: 31,
      byDonationType: [
        { donationTypeId: 'building-fund', amount: 3700, count: 23 },
        { donationTypeId: 'jummah', amount: 700, count: 5 },
        { donationTypeId: 'zakat', amount: 400, count: 3 },
      ],
      byPlatform: [
        { platform: 'apple_pay', amount: 1800, count: 12 },
        { platform: 'google_pay', amount: 1000, count: 8 },
        { platform: 'paypal', amount: 1000, count: 8 },
        { platform: 'bank_transfer', amount: 1000, count: 3 },
      ],
    },
  ],
}

// Time periods for analytics
export type AnalyticsPeriod =
  | 'all_time'
  | 'this_month'
  | 'last_month'
  | 'last_3_months'
  | 'last_6_months'
  | 'last_12_months'
  | 'custom'

export interface AnalyticsPeriodOption {
  value: AnalyticsPeriod
  label: string
}

export const ANALYTICS_PERIOD_OPTIONS: AnalyticsPeriodOption[] = [
  { value: 'all_time', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom Range' },
]

// Overall donation analytics (not tied to any campaign)
export interface OverallAnalytics {
  period: AnalyticsPeriod
  startDate?: string
  endDate?: string
  totalAmount: number
  totalTransactions: number
  byDonationType: DonationBreakdown[]
  byPlatform: PlatformBreakdown[]
}

// Mock overall analytics data
export const MOCK_OVERALL_ANALYTICS: OverallAnalytics[] = [
  {
    period: 'all_time',
    totalAmount: 96500,
    totalTransactions: 906,
    byDonationType: [
      { donationTypeId: 'zakat', amount: 31000, count: 144, byPlatform: [
        { platform: 'apple_pay', amount: 12000, count: 56 },
        { platform: 'google_pay', amount: 8000, count: 38 },
        { platform: 'paypal', amount: 8500, count: 42 },
        { platform: 'bank_transfer', amount: 2500, count: 8 },
      ]},
      { donationTypeId: 'fitrana', amount: 8200, count: 328, byPlatform: [
        { platform: 'apple_pay', amount: 3500, count: 140 },
        { platform: 'google_pay', amount: 2700, count: 108 },
        { platform: 'paypal', amount: 2000, count: 80 },
      ]},
      { donationTypeId: 'jummah', amount: 16300, count: 318, byPlatform: [
        { platform: 'apple_pay', amount: 7000, count: 136 },
        { platform: 'google_pay', amount: 5300, count: 103 },
        { platform: 'paypal', amount: 3500, count: 68 },
        { platform: 'bank_transfer', amount: 500, count: 11 },
      ]},
      { donationTypeId: 'building-fund', amount: 38000, count: 105, byPlatform: [
        { platform: 'apple_pay', amount: 14000, count: 42 },
        { platform: 'google_pay', amount: 8500, count: 26 },
        { platform: 'paypal', amount: 9500, count: 32 },
        { platform: 'bank_transfer', amount: 6000, count: 5 },
      ]},
      { donationTypeId: 'new-carpet', amount: 3000, count: 11, byPlatform: [
        { platform: 'apple_pay', amount: 1500, count: 5 },
        { platform: 'google_pay', amount: 1000, count: 4 },
        { platform: 'paypal', amount: 500, count: 2 },
      ]},
    ],
    byPlatform: [
      { platform: 'apple_pay', amount: 38000, count: 420 },
      { platform: 'google_pay', amount: 25500, count: 280 },
      { platform: 'paypal', amount: 24000, count: 192 },
      { platform: 'bank_transfer', amount: 9000, count: 14 },
    ],
  },
  {
    period: 'this_month',
    totalAmount: 8300,
    totalTransactions: 75,
    byDonationType: [
      { donationTypeId: 'zakat', amount: 1200, count: 8, byPlatform: [
        { platform: 'apple_pay', amount: 500, count: 3 },
        { platform: 'google_pay', amount: 400, count: 3 },
        { platform: 'paypal', amount: 300, count: 2 },
      ]},
      { donationTypeId: 'fitrana', amount: 450, count: 18, byPlatform: [
        { platform: 'apple_pay', amount: 200, count: 8 },
        { platform: 'google_pay', amount: 150, count: 6 },
        { platform: 'paypal', amount: 100, count: 4 },
      ]},
      { donationTypeId: 'jummah', amount: 2350, count: 39, byPlatform: [
        { platform: 'apple_pay', amount: 1000, count: 16 },
        { platform: 'google_pay', amount: 750, count: 13 },
        { platform: 'paypal', amount: 600, count: 10 },
      ]},
      { donationTypeId: 'building-fund', amount: 4200, count: 10, byPlatform: [
        { platform: 'apple_pay', amount: 1300, count: 4 },
        { platform: 'google_pay', amount: 700, count: 2 },
        { platform: 'paypal', amount: 700, count: 2 },
        { platform: 'bank_transfer', amount: 1500, count: 2 },
      ]},
      { donationTypeId: 'new-carpet', amount: 100, count: 0 },
    ],
    byPlatform: [
      { platform: 'apple_pay', amount: 3000, count: 32 },
      { platform: 'google_pay', amount: 2000, count: 22 },
      { platform: 'paypal', amount: 1800, count: 18 },
      { platform: 'bank_transfer', amount: 1500, count: 3 },
    ],
  },
  {
    period: 'last_month',
    totalAmount: 7400,
    totalTransactions: 68,
    byDonationType: [
      { donationTypeId: 'zakat', amount: 950, count: 6, byPlatform: [
        { platform: 'apple_pay', amount: 400, count: 3 },
        { platform: 'google_pay', amount: 300, count: 2 },
        { platform: 'paypal', amount: 250, count: 1 },
      ]},
      { donationTypeId: 'fitrana', amount: 380, count: 15, byPlatform: [
        { platform: 'apple_pay', amount: 180, count: 7 },
        { platform: 'google_pay', amount: 120, count: 5 },
        { platform: 'paypal', amount: 80, count: 3 },
      ]},
      { donationTypeId: 'jummah', amount: 1670, count: 34, byPlatform: [
        { platform: 'apple_pay', amount: 700, count: 14 },
        { platform: 'google_pay', amount: 570, count: 12 },
        { platform: 'paypal', amount: 400, count: 8 },
      ]},
      { donationTypeId: 'building-fund', amount: 4300, count: 13, byPlatform: [
        { platform: 'apple_pay', amount: 1300, count: 4 },
        { platform: 'google_pay', amount: 800, count: 3 },
        { platform: 'paypal', amount: 700, count: 3 },
        { platform: 'bank_transfer', amount: 1500, count: 3 },
      ]},
      { donationTypeId: 'new-carpet', amount: 100, count: 0 },
    ],
    byPlatform: [
      { platform: 'apple_pay', amount: 2600, count: 28 },
      { platform: 'google_pay', amount: 1800, count: 20 },
      { platform: 'paypal', amount: 1500, count: 17 },
      { platform: 'bank_transfer', amount: 1500, count: 3 },
    ],
  },
  {
    period: 'last_3_months',
    totalAmount: 22000,
    totalTransactions: 203,
    byDonationType: [
      { donationTypeId: 'zakat', amount: 5700, count: 30, byPlatform: [
        { platform: 'apple_pay', amount: 2200, count: 12 },
        { platform: 'google_pay', amount: 1500, count: 8 },
        { platform: 'paypal', amount: 1500, count: 8 },
        { platform: 'bank_transfer', amount: 500, count: 2 },
      ]},
      { donationTypeId: 'fitrana', amount: 1800, count: 72, byPlatform: [
        { platform: 'apple_pay', amount: 800, count: 32 },
        { platform: 'google_pay', amount: 600, count: 24 },
        { platform: 'paypal', amount: 400, count: 16 },
      ]},
      { donationTypeId: 'jummah', amount: 5600, count: 106, byPlatform: [
        { platform: 'apple_pay', amount: 2400, count: 45 },
        { platform: 'google_pay', amount: 1800, count: 34 },
        { platform: 'paypal', amount: 1400, count: 27 },
      ]},
      { donationTypeId: 'building-fund', amount: 8600, count: 22, byPlatform: [
        { platform: 'apple_pay', amount: 2700, count: 8 },
        { platform: 'google_pay', amount: 1500, count: 5 },
        { platform: 'paypal', amount: 1400, count: 4 },
        { platform: 'bank_transfer', amount: 3000, count: 5 },
      ]},
      { donationTypeId: 'new-carpet', amount: 300, count: 3, byPlatform: [
        { platform: 'apple_pay', amount: 100, count: 1 },
        { platform: 'google_pay', amount: 100, count: 1 },
        { platform: 'paypal', amount: 100, count: 1 },
      ]},
    ],
    byPlatform: [
      { platform: 'apple_pay', amount: 8200, count: 90 },
      { platform: 'google_pay', amount: 5500, count: 60 },
      { platform: 'paypal', amount: 4800, count: 45 },
      { platform: 'bank_transfer', amount: 3500, count: 8 },
    ],
  },
  {
    period: 'last_6_months',
    totalAmount: 44200,
    totalTransactions: 422,
    byDonationType: [
      { donationTypeId: 'zakat', amount: 14000, count: 66, byPlatform: [
        { platform: 'apple_pay', amount: 5500, count: 26 },
        { platform: 'google_pay', amount: 3700, count: 18 },
        { platform: 'paypal', amount: 3800, count: 18 },
        { platform: 'bank_transfer', amount: 1000, count: 4 },
      ]},
      { donationTypeId: 'fitrana', amount: 3600, count: 144, byPlatform: [
        { platform: 'apple_pay', amount: 1600, count: 64 },
        { platform: 'google_pay', amount: 1200, count: 48 },
        { platform: 'paypal', amount: 800, count: 32 },
      ]},
      { donationTypeId: 'jummah', amount: 10300, count: 200, byPlatform: [
        { platform: 'apple_pay', amount: 4400, count: 85 },
        { platform: 'google_pay', amount: 3300, count: 65 },
        { platform: 'paypal', amount: 2600, count: 50 },
      ]},
      { donationTypeId: 'building-fund', amount: 15200, count: 39, byPlatform: [
        { platform: 'apple_pay', amount: 5500, count: 15 },
        { platform: 'google_pay', amount: 3200, count: 9 },
        { platform: 'paypal', amount: 1500, count: 7 },
        { platform: 'bank_transfer', amount: 5000, count: 8 },
      ]},
      { donationTypeId: 'new-carpet', amount: 1100, count: 5, byPlatform: [
        { platform: 'apple_pay', amount: 500, count: 2 },
        { platform: 'google_pay', amount: 400, count: 2 },
        { platform: 'paypal', amount: 200, count: 1 },
      ]},
    ],
    byPlatform: [
      { platform: 'apple_pay', amount: 17500, count: 192 },
      { platform: 'google_pay', amount: 11800, count: 130 },
      { platform: 'paypal', amount: 8900, count: 90 },
      { platform: 'bank_transfer', amount: 6000, count: 10 },
    ],
  },
  {
    period: 'last_12_months',
    totalAmount: 80400,
    totalTransactions: 759,
    byDonationType: [
      { donationTypeId: 'zakat', amount: 26300, count: 122, byPlatform: [
        { platform: 'apple_pay', amount: 10200, count: 48 },
        { platform: 'google_pay', amount: 6800, count: 32 },
        { platform: 'paypal', amount: 7300, count: 36 },
        { platform: 'bank_transfer', amount: 2000, count: 6 },
      ]},
      { donationTypeId: 'fitrana', amount: 6800, count: 272, byPlatform: [
        { platform: 'apple_pay', amount: 3000, count: 120 },
        { platform: 'google_pay', amount: 2300, count: 92 },
        { platform: 'paypal', amount: 1500, count: 60 },
      ]},
      { donationTypeId: 'jummah', amount: 13700, count: 268, byPlatform: [
        { platform: 'apple_pay', amount: 5900, count: 115 },
        { platform: 'google_pay', amount: 4400, count: 86 },
        { platform: 'paypal', amount: 3400, count: 67 },
      ]},
      { donationTypeId: 'building-fund', amount: 31200, count: 86, byPlatform: [
        { platform: 'apple_pay', amount: 11400, count: 33 },
        { platform: 'google_pay', amount: 7200, count: 21 },
        { platform: 'paypal', amount: 6600, count: 22 },
        { platform: 'bank_transfer', amount: 6000, count: 10 },
      ]},
      { donationTypeId: 'new-carpet', amount: 2400, count: 9, byPlatform: [
        { platform: 'apple_pay', amount: 1000, count: 4 },
        { platform: 'google_pay', amount: 800, count: 3 },
        { platform: 'paypal', amount: 600, count: 2 },
      ]},
    ],
    byPlatform: [
      { platform: 'apple_pay', amount: 32000, count: 350 },
      { platform: 'google_pay', amount: 21500, count: 238 },
      { platform: 'paypal', amount: 18900, count: 157 },
      { platform: 'bank_transfer', amount: 8000, count: 14 },
    ],
  },
]

// Helper to generate mock data for custom date ranges
export const getMockCustomAnalytics = (startDate: string, endDate: string): OverallAnalytics => {
  // Simple mock: scale based on number of days in range
  const start = new Date(startDate)
  const end = new Date(endDate)
  const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)))
  const scale = days / 365 // Scale relative to a full year

  return {
    period: 'custom',
    startDate,
    endDate,
    totalAmount: Math.round(80400 * scale),
    totalTransactions: Math.round(759 * scale),
    byDonationType: [
      { donationTypeId: 'zakat', amount: Math.round(26300 * scale), count: Math.round(122 * scale), byPlatform: [
        { platform: 'apple_pay', amount: Math.round(10200 * scale), count: Math.round(48 * scale) },
        { platform: 'google_pay', amount: Math.round(6800 * scale), count: Math.round(32 * scale) },
        { platform: 'paypal', amount: Math.round(7300 * scale), count: Math.round(36 * scale) },
        { platform: 'bank_transfer', amount: Math.round(2000 * scale), count: Math.round(6 * scale) },
      ]},
      { donationTypeId: 'fitrana', amount: Math.round(6800 * scale), count: Math.round(272 * scale), byPlatform: [
        { platform: 'apple_pay', amount: Math.round(3000 * scale), count: Math.round(120 * scale) },
        { platform: 'google_pay', amount: Math.round(2300 * scale), count: Math.round(92 * scale) },
        { platform: 'paypal', amount: Math.round(1500 * scale), count: Math.round(60 * scale) },
      ]},
      { donationTypeId: 'jummah', amount: Math.round(13700 * scale), count: Math.round(268 * scale), byPlatform: [
        { platform: 'apple_pay', amount: Math.round(5900 * scale), count: Math.round(115 * scale) },
        { platform: 'google_pay', amount: Math.round(4400 * scale), count: Math.round(86 * scale) },
        { platform: 'paypal', amount: Math.round(3400 * scale), count: Math.round(67 * scale) },
      ]},
      { donationTypeId: 'building-fund', amount: Math.round(31200 * scale), count: Math.round(86 * scale), byPlatform: [
        { platform: 'apple_pay', amount: Math.round(11400 * scale), count: Math.round(33 * scale) },
        { platform: 'google_pay', amount: Math.round(7200 * scale), count: Math.round(21 * scale) },
        { platform: 'paypal', amount: Math.round(6600 * scale), count: Math.round(22 * scale) },
        { platform: 'bank_transfer', amount: Math.round(6000 * scale), count: Math.round(10 * scale) },
      ]},
      { donationTypeId: 'new-carpet', amount: Math.round(2400 * scale), count: Math.round(9 * scale), byPlatform: [
        { platform: 'apple_pay', amount: Math.round(1000 * scale), count: Math.round(4 * scale) },
        { platform: 'google_pay', amount: Math.round(800 * scale), count: Math.round(3 * scale) },
        { platform: 'paypal', amount: Math.round(600 * scale), count: Math.round(2 * scale) },
      ]},
    ],
    byPlatform: [
      { platform: 'apple_pay', amount: Math.round(32000 * scale), count: Math.round(350 * scale) },
      { platform: 'google_pay', amount: Math.round(21500 * scale), count: Math.round(238 * scale) },
      { platform: 'paypal', amount: Math.round(18900 * scale), count: Math.round(157 * scale) },
      { platform: 'bank_transfer', amount: Math.round(8000 * scale), count: Math.round(14 * scale) },
    ],
  }
}

// Transaction represents any donation from any platform
export interface Transaction {
  id: string
  amount: number
  platform: PaymentPlatform
  device?: DeviceType // ios, android, web - used with stripe to determine Apple Pay vs Google Pay
  donationTypeId: string
  campaignId?: string
  date: string // ISO date string
  reference?: string // payment reference or notes
  createdAt: string
}

// Mock transactions data (combining all platforms)
export const MOCK_TRANSACTIONS: Transaction[] = [
  // Bank transfers
  {
    id: 'txn-bank-1',
    amount: 5000,
    platform: 'bank_transfer',
    donationTypeId: 'building-fund',
    campaignId: 'new_masjid_2024',
    date: '2024-02-15',
    reference: 'Bank ref: BLDG-2024-001',
    createdAt: '2024-02-16T10:30:00Z',
  },
  {
    id: 'txn-bank-2',
    amount: 2500,
    platform: 'bank_transfer',
    donationTypeId: 'zakat',
    date: '2024-02-20',
    reference: 'Anonymous donor - Zakat',
    createdAt: '2024-02-21T14:15:00Z',
  },
  {
    id: 'txn-bank-3',
    amount: 1000,
    platform: 'bank_transfer',
    donationTypeId: 'building-fund',
    campaignId: 'new_masjid_2024',
    date: '2024-03-01',
    reference: 'Monthly standing order',
    createdAt: '2024-03-02T09:00:00Z',
  },
  // Apple Pay transactions
  {
    id: 'txn-apple-1',
    amount: 50,
    platform: 'stripe',
    donationTypeId: 'jummah',
    date: '2024-03-08',
    reference: 'Stripe TXN-AP8A3F',
    createdAt: '2024-03-08T13:25:00Z',
  },
  {
    id: 'txn-apple-2',
    amount: 200,
    platform: 'stripe',
    donationTypeId: 'building-fund',
    campaignId: 'new_masjid_2024',
    date: '2024-03-06',
    reference: 'Stripe TXN-AP6C2D',
    createdAt: '2024-03-06T18:45:00Z',
  },
  {
    id: 'txn-apple-3',
    amount: 75,
    platform: 'stripe',
    donationTypeId: 'jummah',
    date: '2024-03-01',
    reference: 'Stripe TXN-AP1E5F',
    createdAt: '2024-03-01T13:10:00Z',
  },
  {
    id: 'txn-apple-4',
    amount: 100,
    platform: 'stripe',
    donationTypeId: 'zakat',
    date: '2024-02-28',
    reference: 'Stripe TXN-AP2B4A',
    createdAt: '2024-02-28T15:20:00Z',
  },
  // Google Pay transactions
  {
    id: 'txn-google-1',
    amount: 100,
    platform: 'stripe',
    donationTypeId: 'zakat',
    date: '2024-03-07',
    reference: 'Stripe TXN-GP7D1E',
    createdAt: '2024-03-07T10:15:00Z',
  },
  {
    id: 'txn-google-2',
    amount: 25,
    platform: 'stripe',
    donationTypeId: 'fitrana',
    date: '2024-03-05',
    reference: 'Stripe TXN-GP5F3A',
    createdAt: '2024-03-05T09:30:00Z',
  },
  {
    id: 'txn-google-3',
    amount: 50,
    platform: 'stripe',
    donationTypeId: 'jummah',
    date: '2024-03-03',
    reference: 'Stripe TXN-GP3B2C',
    createdAt: '2024-03-03T14:00:00Z',
  },
  {
    id: 'txn-google-4',
    amount: 150,
    platform: 'stripe',
    donationTypeId: 'building-fund',
    campaignId: 'new_masjid_2024',
    date: '2024-02-25',
    reference: 'Stripe TXN-GP2A1B',
    createdAt: '2024-02-25T11:30:00Z',
  },
  // PayPal transactions
  {
    id: 'txn-paypal-1',
    amount: 150,
    platform: 'paypal',
    donationTypeId: 'zakat',
    date: '2024-03-07',
    reference: 'PayPal TXN-PP7X2Y',
    createdAt: '2024-03-07T16:30:00Z',
  },
  {
    id: 'txn-paypal-2',
    amount: 500,
    platform: 'paypal',
    donationTypeId: 'building-fund',
    campaignId: 'new_masjid_2024',
    date: '2024-03-05',
    reference: 'PayPal TXN-PP5Z3W',
    createdAt: '2024-03-05T20:15:00Z',
  },
  {
    id: 'txn-paypal-3',
    amount: 25,
    platform: 'paypal',
    donationTypeId: 'fitrana',
    date: '2024-03-04',
    reference: 'PayPal TXN-PP4V1U',
    createdAt: '2024-03-04T11:00:00Z',
  },
  {
    id: 'txn-paypal-4',
    amount: 75,
    platform: 'paypal',
    donationTypeId: 'jummah',
    date: '2024-03-02',
    reference: 'PayPal TXN-PP2T5S',
    createdAt: '2024-03-02T08:45:00Z',
  },
]

// Default donation types matching iOS app
export const DEFAULT_DONATION_TYPES: DonationType[] = [
  {
    id: 'zakat',
    name: 'Zakat',
    description: 'Obligatory charity - 2.5% of savings above Nisab',
    iconName: '',
    suggestedAmounts: [50, 100, 250, 500],
    minimumAmount: 1,
    allowCustomAmount: true,
    allowRecurring: false,
    recurringFrequencies: [],
    isActive: true,
    order: 1,
  },
  {
    id: 'fitrana',
    name: 'Fitrana',
    description: 'Zakat al-Fitr - given before Eid prayer',
    iconName: '',
    suggestedAmounts: [5, 10, 20, 50],
    minimumAmount: 5,
    allowCustomAmount: true,
    allowRecurring: false,
    recurringFrequencies: [],
    isActive: true,
    order: 2,
  },
  {
    id: 'jummah',
    name: 'Jummah Donations',
    description: 'Weekly Friday prayer contributions',
    iconName: '',
    suggestedAmounts: [10, 20, 50, 100],
    minimumAmount: 1,
    allowCustomAmount: true,
    allowRecurring: false,
    recurringFrequencies: [],
    isActive: true,
    order: 3,
  },
  {
    id: 'building-fund',
    name: 'New Building Fund',
    description: 'Support the new masjid construction',
    iconName: '',
    suggestedAmounts: [25, 50, 100, 200],
    minimumAmount: 10,
    allowCustomAmount: true,
    allowRecurring: false,
    recurringFrequencies: [],
    isActive: true,
    order: 4,
  },
  {
    id: 'new-carpet',
    name: 'New Carpet',
    description: 'Contribute towards new masjid carpet',
    iconName: '',
    suggestedAmounts: [10, 25, 50, 100],
    minimumAmount: 5,
    allowCustomAmount: true,
    allowRecurring: false,
    recurringFrequencies: [],
    isActive: false,
    order: 5,
  },
]
